import React from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, Skull, Users, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { LanguageSelector } from '../components/LanguageSelector';
import { VotingResults, Player } from '../types';

interface ResultsPageProps {
  results: VotingResults;
  players: Player[];
  word: string;
  category: string;
  onBack: () => void;
  onPlayAgain: () => void;
  onGoHome: () => void;
}

export const ResultsPage: React.FC<ResultsPageProps> = ({
  results,
  players,
  word,
  category,
  onBack,
  onPlayAgain,
  onGoHome,
}) => {
  const { t } = useTranslation();

  const impostorPlayers = players.filter(p => results.impostorIds.includes(p.id));
  const mostVotedPlayer = players.find(p => p.id === results.mostVoted);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            {t('results.title')}
          </h1>
        </div>

        {/* Result Card */}
        <Card className={`mb-6 border-2 ${
          results.isImpostorCaught 
            ? 'border-success-400 bg-success-500/10' 
            : 'border-accent-400 bg-accent-500/10'
        }`}>
          <div className="text-center">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
              results.isImpostorCaught 
                ? 'bg-success-500' 
                : 'bg-accent-500'
            }`}>
              {results.isImpostorCaught ? (
                <Trophy className="w-10 h-10 text-white" />
              ) : (
                <Skull className="w-10 h-10 text-white" />
              )}
            </div>
            
            <h2 className={`text-3xl font-bold mb-4 ${
              results.isImpostorCaught 
                ? 'text-success-400' 
                : 'text-accent-400'
            }`}>
              {results.isImpostorCaught 
                ? '🎉 ¡Los jugadores ganan!'
                : '😈 ¡El impostor se salió con la suya!'
              }
            </h2>
            
            <div className="space-y-3">
              <div>
                <p className="text-white/80 text-lg mb-2">
                  {results.impostorIds.length === 1 ? 'El impostor era:' : 'Los impostores eran:'}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {impostorPlayers.map((impostor) => (
                    <span key={impostor.id} className="px-4 py-2 bg-accent-500/30 border border-accent-400 rounded-lg text-white font-semibold">
                      {impostor.name} 😈
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-white/80 text-lg">
                La palabra era: <strong className="text-white uppercase">{word}</strong>
              </p>
              <p className="text-white/80 text-lg capitalize">
                Categoría: <strong className="text-white">{category}</strong>
              </p>
            </div>
          </div>
        </Card>

        {/* Vote Breakdown */}
        <Card className="mb-6">
          <h3 className="text-xl font-semibold text-white mb-6 text-center">
            Desglose de Votos
          </h3>
          
          <div className="space-y-3">
            {Object.entries(results.voteCounts).map(([playerId, votes]) => {
              const player = players.find(p => p.id === playerId);
              const isMostVoted = playerId === results.mostVoted;
              const isImpostor = results.impostorIds.includes(playerId);
              
              return (
                <div
                  key={playerId}
                  className={`p-4 rounded-xl border-2 ${
                    isMostVoted
                      ? 'bg-accent-500/20 border-accent-400'
                      : 'bg-white/5 border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                        isImpostor 
                          ? 'bg-red-500' 
                          : 'bg-white/20'
                      }`}>
                        {player?.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-semibold">
                          {player?.name}
                          {isImpostor && ' 👹'}
                          {isMostVoted && ' 🎯'}
                        </p>
                        <p className="text-white/60 text-sm">
                          {isImpostor ? 'Impostor' : 'Jugador'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-white">
                        {votes}
                      </p>
                      <p className="text-white/60 text-sm">
                        {votes === 1 ? 'voto' : 'votos'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={onGoHome}
            icon={<Home className="w-5 h-5" />}
          >
            Volver al Inicio
          </Button>
        </div>
      </div>
    </div>
  );
};
